// Enhanced AdminNews.jsx with green theme matching AdminPackages
import React, { useState, useEffect } from 'react';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';
import RequireAdminRole from '../../components/auth/userole';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  FileText,
  Calendar,
  User,
  CheckCircle,
  Clock,
  Archive,
  ChevronDown,
  ChevronUp,
  Plus,
  TrendingUp,
  Star,
  Tag,
  Globe
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const STATUS_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' }
];

const CATEGORY_OPTIONS = [
  { value: 'Technology', label: 'Technology' },
  { value: 'Business', label: 'Business' },
  { value: 'General', label: 'General' },
  { value: 'Infrastructure', label: 'Infrastructure' },
  { value: 'Community', label: 'Community' }
];

const AdminNewsPage = () => {
  const { user, loading } = useAuth();
  const { user: contextUser, token: contextToken } = useAuth();
  
  const [activeTab, setActiveTab] = useState('news');
  const [newsList, setNewsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: contextUser?.fullName || contextUser?.name || 'Admin User',
    email: contextUser?.email || 'admin@telnet.co.id',
    role: contextUser?.role || 'Super Admin'
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    summary: '',
    content: '',
    image: '',
    category: 'Technology',
    status: 'draft',
    isHeadline: false,
    isFeatured: false,
    tags: '',
    seoTitle: '',
    seoDescription: ''
  });

  const LIMIT = 10;

  // Enhanced token handling
  const authToken = contextToken ||
                   localStorage.getItem('adminToken') ||
                   localStorage.getItem('token') ||
                   sessionStorage.getItem('adminToken') ||
                   sessionStorage.getItem('token');

  // Enhanced auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge color with icons
  const getStatusColor = (status) => {
    const colors = {
      published: 'bg-green-100 text-green-800 border-green-200',
      draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      archived: 'bg-gray-300 text-gray-800 border-gray-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      published: <CheckCircle size={12} className="text-green-600" />,
      draft: <Clock size={12} className="text-yellow-600" />,
      archived: <Archive size={12} className="text-gray-600" />
    };
    return icons[status] || <Clock size={12} className="text-gray-600" />;
  };

  // Enhanced fetch news with better error handling
  const fetchNews = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: LIMIT.toString(),
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search })
      });

      const headers = getAuthHeaders();
      const adminNewsUrl = `${API_BASE_URL}/api/news?${params.toString()}`;
      
      const response = await fetch(adminNewsUrl, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view news.');
        } else if (response.status === 404) {
          throw new Error('News endpoint not found. Please check your API configuration.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch news`);
        }
      }

      const data = await response.json();

      if (data.success) {
        const newsData = Array.isArray(data.data) ? data.data : Array.isArray(data.data?.news) ? data.data.news : [];
        setNewsList(newsData);
        setTotalPages(data.pagination?.totalPages || data.data?.pagination?.totalPages || Math.ceil(newsData.length / LIMIT) || 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load news');
      }
    } catch (error) {
      let errorMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setNewsList([]);
      setTotalPages(1);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Create news
  const handleCreateNews = async () => {
    try {
      const newsData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        author: {
          name: currentUser.name,
          image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face'
        }
      };

      const response = await fetch(`${API_BASE_URL}/api/news`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(newsData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to create news`);
      }

      const data = await response.json();

      if (data.success) {
        alert('Berita berhasil dibuat!');
        setShowCreateModal(false);
        resetFormData();
        fetchNews();
      } else {
        throw new Error(data.message || 'Failed to create news');
      }
    } catch (error) {
      console.error('Error creating news:', error);
      alert('Error creating news: ' + error.message);
    }
  };

  // Update news
  const handleUpdateNews = async () => {
    if (!selectedNews) return;

    try {
      const newsData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      const response = await fetch(`${API_BASE_URL}/api/news/${selectedNews._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(newsData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update news`);
      }

      const data = await response.json();

      if (data.success) {
        alert('Berita berhasil diupdate!');
        setShowEditModal(false);
        setSelectedNews(null);
        resetFormData();
        fetchNews();
      } else {
        throw new Error(data.message || 'Failed to update news');
      }
    } catch (error) {
      console.error('Error updating news:', error);
      alert('Error updating news: ' + error.message);
    }
  };

  // Delete news
  const handleDeleteNews = async () => {
    if (!selectedNews) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/news/${selectedNews._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to delete news`);
      }

      const data = await response.json();

      if (data.success) {
        alert('Berita berhasil dihapus!');
        setShowDeleteModal(false);
        setSelectedNews(null);
        fetchNews();
      } else {
        throw new Error(data.message || 'Failed to delete news');
      }
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Error deleting news: ' + error.message);
    }
  };

  // Reset form data
  const resetFormData = () => {
    setFormData({
      title: '',
      subtitle: '',
      summary: '',
      content: '',
      image: '',
      category: 'Technology',
      status: 'draft',
      isHeadline: false,
      isFeatured: false,
      tags: '',
      seoTitle: '',
      seoDescription: ''
    });
  };

  // Open modals
  const openCreateModal = () => {
    resetFormData();
    setShowCreateModal(true);
  };

  const openEditModal = (news) => {
    setSelectedNews(news);
    setFormData({
      title: news.title || '',
      subtitle: news.subtitle || '',
      summary: news.summary || '',
      content: news.content || '',
      image: news.image || '',
      category: news.category || 'Technology',
      status: news.status || 'draft',
      isHeadline: news.isHeadline || false,
      isFeatured: news.isFeatured || false,
      tags: news.tags ? news.tags.join(', ') : '',
      seoTitle: news.seoTitle || '',
      seoDescription: news.seoDescription || ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (news) => {
    setSelectedNews(news);
    setShowDeleteModal(true);
  };

  const openDetailModal = (news) => {
    setSelectedNews(news);
    setShowDetailModal(true);
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  useEffect(() => {
    fetchNews();
  }, [page, statusFilter, authToken]);

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchNews();
  };

  // Pagination controls
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Retry function for failed requests
  const handleRetry = () => {
    fetchNews();
  };

  // Stats Cards Component with green theme
  const StatsCards = () => {
    const stats = {
      total: newsList.length,
      published: newsList.filter(news => news.status === 'published').length,
      draft: newsList.filter(news => news.status === 'draft').length,
      featured: newsList.filter(news => news.isFeatured).length
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total News</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Published</p>
              <p className="text-2xl font-bold text-gray-900">{stats.published}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Draft</p>
              <p className="text-2xl font-bold text-gray-900">{stats.draft}</p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Featured</p>
              <p className="text-2xl font-bold text-gray-900">{stats.featured}</p>
              <button 
                onClick={fetchNews}
                disabled={isLoading}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
              >
                <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <Star className="text-purple-500" size={32} />
          </div>
        </div>
      </div>
    );
  };

  // Mobile Header Component with green theme
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Manage News</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {error && (
            <button
              onClick={handleRetry}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition text-xs"
            >
              Retry
            </button>
          )}
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // Mobile News Item Component with green theme
  const MobileNewsItem = ({ news }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => openDetailModal(news)}
            className="text-sm font-medium text-green-600 hover:text-green-800 truncate block text-left"
          >
            {news.title}
          </button>
          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-1">
            <FileText size={12} />
            <span className="truncate">{news.slug}</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {news.isHeadline && (
              <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                <TrendingUp size={10} />
                <span>Headline</span>
              </span>
            )}
            {news.isFeatured && (
              <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                <Star size={10} />
                <span>Featured</span>
              </span>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(news.status)} ml-2`}>
          {getStatusIcon(news.status)}
          <span>
            {news.status === 'published' ? 'Published' : 
             news.status === 'draft' ? 'Draft' : 
             news.status === 'archived' ? 'Archived' : 
             news.status || '-'}
          </span>
        </span>
      </div>
      
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Tag size={12} />
          <span>{news.category}</span>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Eye size={12} />
          <span>{news.views || 0} views</span>
        </div>
      </div>
      
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <Calendar size={10} />
          <span>{news.publishedAt ? formatDate(news.publishedAt) : '-'}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => openEditModal(news)}
            className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
          >
            <Edit size={12} />
            <span>Edit</span>
          </button>
          <button
            onClick={() => openDetailModal(news)}
            className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
          >
            <Eye size={12} />
            <span>Detail</span>
          </button>
          <button
            onClick={() => openDeleteModal(news)}
            className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
          >
            <Trash2 size={12} />
            <span>Hapus</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Desktop Table Component with green theme
  const DesktopTable = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Judul</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tanggal Publikasi</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {newsList.map((news) => (
            <tr key={news._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 text-sm">
                <div className="max-w-xs">
                  <button
                    onClick={() => openDetailModal(news)}
                    className="font-medium text-green-600 hover:text-green-800 truncate block text-left"
                  >
                    {news.title}
                  </button>
                  <div className="text-gray-500 text-xs">
                    {news.slug}
                  </div>
                  <div className="flex mt-1 space-x-1">
                    {news.isHeadline && (
                      <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                        <TrendingUp size={10} />
                        <span>Headline</span>
                      </span>
                    )}
                    {news.isFeatured && (
                      <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                        <Star size={10} />
                        <span>Featured</span>
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Tag size={14} className="text-gray-400" />
                  <span>{news.category}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(news.status)}`}>
                  {getStatusIcon(news.status)}
                  <span>
                    {news.status === 'published' ? 'Published' : 
                     news.status === 'draft' ? 'Draft' : 
                     news.status === 'archived' ? 'Archived' : 
                     news.status || '-'}
                  </span>
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Calendar size={14} className="text-gray-400" />
                  <span>{news.publishedAt ? formatDate(news.publishedAt) : '-'}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Eye size={14} className="text-gray-400" />
                  <span>{news.views || 0}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex space-x-1 justify-center">
                  <button
                    onClick={() => openEditModal(news)}
                    className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
                    title="Edit News"
                  >
                    <Edit size={12} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => openDetailModal(news)}
                    className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
                    title="View Details"
                  >
                    <Eye size={12} />
                    <span>Detail</span>
                  </button>
                  <button
                    onClick={() => openDeleteModal(news)}
                    className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
                    title="Delete News"
                  >
                    <Trash2 size={12} />
                    <span>Hapus</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <RequireAdminRole user={user} loading={loading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />

          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {/* Main Panel dengan green theme */}
            <div className="bg-white rounded-lg shadow-xl">
              {/* Header dengan gradient hijau */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <FileText className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h1 className="font-bold text-xl">News Management</h1>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-green-100">
                        {newsList.length} news ditemukan • Page {page} dari {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchNews}
                    disabled={isLoading}
                    className="hover:bg-green-700 p-2 rounded transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={openCreateModal}
                    className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus size={20} />
                    <span>Tambah Berita</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-200">
                <StatsCards />
              </div>

              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Cari berdasarkan judul atau konten..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </form>

                  {/* Filter Toggle untuk Mobile */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {/* Filters */}
                  <div className={`flex flex-col lg:flex-row gap-2 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              )}

              {/* News Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={32} className="animate-spin text-green-600 mb-2" />
                      <span className="text-gray-500">Loading news...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center max-w-md mx-auto">
                      <div className="text-red-600 mb-4">
                        <span className="text-4xl">⚠️</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load News</h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : newsList.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <FileText size={64} className="mx-auto opacity-30" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No News Found</h3>
                    <p className="text-gray-600">
                      {search || statusFilter ? 'Try adjusting your search filters' : 'No news articles have been created yet'}
                    </p>
                    <button
                      onClick={openCreateModal}
                      className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                    >
                      Create First News
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="sm:hidden">
                      {newsList.map((news) => (
                        <MobileNewsItem key={news._id} news={news} />
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <DesktopTable />
                    </div>
                  </>
                )}

                {/* Pagination */}
                {!error && !isLoading && newsList.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Halaman {page} dari {totalPages} ({newsList.length} news)
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berikutnya
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Create News Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Tambah Berita Baru</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    >
                      {CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.slice(1).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image URL *</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Summary *</label>
                    <textarea
                      value={formData.summary}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      rows="3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      rows="8"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="tag1, tag2, tag3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SEO Title</label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) => setFormData({...formData, seoTitle: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      maxLength="60"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">SEO Description</label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={(e) => setFormData({...formData, seoDescription: e.target.value})}
                      rows="2"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      maxLength="160"
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isHeadline}
                        onChange={(e) => setFormData({...formData, isHeadline: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Headline</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isFeatured}
                        onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Featured</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateNews}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Create News
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit News Modal */}
        {showEditModal && selectedNews && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Edit Berita</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    >
                      {CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.slice(1).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image URL *</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Summary *</label>
                    <textarea
                      value={formData.summary}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      rows="3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      rows="8"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="tag1, tag2, tag3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SEO Title</label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) => setFormData({...formData, seoTitle: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      maxLength="60"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">SEO Description</label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={(e) => setFormData({...formData, seoDescription: e.target.value})}
                      rows="2"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      maxLength="160"
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isHeadline}
                        onChange={(e) => setFormData({...formData, isHeadline: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Headline</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isFeatured}
                        onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Featured</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateNews}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update News
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedNews && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto">
              <div className="p-4 sm:p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Delete News</h3>
                <p className="text-sm text-gray-700 mb-6">
                  Are you sure you want to delete "<strong>{selectedNews.title}</strong>"? This action cannot be undone.
                </p>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteNews}
                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm sm:text-base"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* News Detail Modal */}
        {showDetailModal && selectedNews && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">News Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <FileText size={18} />
                      <span>Informasi Dasar</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Title:</span> {selectedNews.title}</div>
                      <div><span className="font-medium">Slug:</span> {selectedNews.slug}</div>
                      {selectedNews.subtitle && (
                        <div><span className="font-medium">Subtitle:</span> {selectedNews.subtitle}</div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Tag size={14} className="text-gray-400" />
                        <span><span className="font-medium">Category:</span> {selectedNews.category}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Status:</span>
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedNews.status)}`}>
                          {getStatusIcon(selectedNews.status)}
                          <span>{selectedNews.status}</span>
                        </span>
                      </div>
                      <div className="flex space-x-4">
                        {selectedNews.isHeadline && (
                          <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                            <TrendingUp size={10} />
                            <span>Headline</span>
                          </span>
                        )}
                        {selectedNews.isFeatured && (
                          <span className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                            <Star size={10} />
                            <span>Featured</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <TrendingUp size={18} />
                      <span>Statistics</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Eye size={14} className="text-gray-400" />
                        <span><span className="font-medium">Views:</span> {selectedNews.views || 0}</span>
                      </div>
                      <div><span className="font-medium">Likes:</span> {selectedNews.likes || 0}</div>
                      <div><span className="font-medium">Comments:</span> {selectedNews.comments || 0}</div>
                      <div><span className="font-medium">Read Time:</span> {selectedNews.readTime || 5} minutes</div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Calendar size={18} />
                      <span>Dates</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Created:</span> {formatDateTime(selectedNews.createdAt)}</div>
                      <div><span className="font-medium">Updated:</span> {formatDateTime(selectedNews.updatedAt)}</div>
                      <div><span className="font-medium">Published:</span> {formatDateTime(selectedNews.publishedAt)}</div>
                    </div>
                  </div>

                  {/* Author Info */}
                  {selectedNews.author && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <User size={18} />
                        <span>Author</span>
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Name:</span> {selectedNews.author.name}</div>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {selectedNews.tags && selectedNews.tags.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <Tag size={18} />
                        <span>Tags</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedNews.tags.map((tag, idx) => (
                          <span key={idx} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-800 border border-gray-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SEO Info */}
                  {(selectedNews.seoTitle || selectedNews.seoDescription) && (
                    <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <Globe size={18} />
                        <span>SEO Information</span>
                      </h4>
                      <div className="space-y-2 text-sm">
                        {selectedNews.seoTitle && (
                          <div><span className="font-medium">SEO Title:</span> {selectedNews.seoTitle}</div>
                        )}
                        {selectedNews.seoDescription && (
                          <div><span className="font-medium">SEO Description:</span> {selectedNews.seoDescription}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Summary</h4>
                    <p className="text-sm text-gray-700">{selectedNews.summary}</p>
                  </div>

                  {/* Image */}
                  {selectedNews.image && (
                    <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Featured Image</h4>
                      <img 
                        src={selectedNews.image} 
                        alt={selectedNews.title}
                        className="w-full max-w-md h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {/* Content Preview */}
                  <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Content Preview</h4>
                    <div 
                      className="text-sm text-gray-700 max-h-40 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: selectedNews.content?.substring(0, 500) + '...' }}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      openEditModal(selectedNews);
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Edit News
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdminRole>
  );
};

export default AdminNewsPage;